<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { numeric, minValue } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';

import NextSelect from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  threshold: {
    type: Number,
    default: null,
  },
  thresholdUnit: {
    type: String,
    default: 'Minutes',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['unit', 'isInValid', 'updateThreshold']);

const { t } = useI18n();

const thresholdTime = ref('');
const thresholdUnitValue = ref(props.thresholdUnit);

const rules = {
  thresholdTime: {
    numeric,
    minValue: minValue(0.001),
  },
};

const v$ = useVuelidate(rules, { thresholdTime }, { $stopPropagation: true });

const options = computed(() => [
  { value: 'Minutes', label: t('SLA.FORM.TIME_UNITS.MINUTES') },
  { value: 'Hours', label: t('SLA.FORM.TIME_UNITS.HOURS') },
  { value: 'Days', label: t('SLA.FORM.TIME_UNITS.DAYS') },
]);

const thresholdTimeErrorMessage = computed(() => {
  if (!v$.value.thresholdTime.$error) return '';

  if (
    v$.value.thresholdTime.numeric?.$invalid ||
    v$.value.thresholdTime.minValue?.$invalid
  ) {
    return t('SLA.FORM.THRESHOLD_TIME.INVALID_FORMAT_ERROR');
  }

  return '';
});

const onThresholdUnitChange = () => {
  emit('unit', thresholdUnitValue.value);
};

const onThresholdTimeChange = () => {
  v$.value.thresholdTime.$touch();
  emit('isInValid', v$.value.thresholdTime.$invalid);
  emit(
    'updateThreshold',
    thresholdTime.value ? Number(thresholdTime.value) : null
  );
};

watch(
  () => props.threshold,
  value => {
    thresholdTime.value =
      typeof value === 'number' && !Number.isNaN(value) ? value : '';
  },
  { immediate: true }
);

watch(
  () => props.thresholdUnit,
  value => {
    thresholdUnitValue.value = value;
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex items-center w-full gap-3">
    <woot-input
      v-model="thresholdTime"
      type="number"
      :class="{ error: v$.thresholdTime.$error }"
      class="flex-grow"
      :label="label"
      :placeholder="placeholder"
      :error="thresholdTimeErrorMessage"
      @update:model-value="onThresholdTimeChange"
    />
    <div class="mt-7 w-28 shrink-0">
      <NextSelect
        v-model="thresholdUnitValue"
        :options="options"
        @update:model-value="onThresholdUnitChange"
      />
    </div>
  </div>
</template>
